import { ExclamationCircleIcon } from '@heroicons/react/24/solid';
import { cn } from '@theprintbar/shared';
import { useField } from 'remix-validated-form';
import { FieldProps } from '../index';

interface TextAreaProps extends FieldProps<HTMLTextAreaElement> {
	rows?: number;
	placeholder?: string;
	className?: string;
	compact?: boolean;
	labelClassName?: string;
	textAreaClassName?: string;
	fieldRef?: React.Ref<HTMLTextAreaElement>;
}

export function TextArea({
	label,
	name,
	rows,
	placeholder,
	className,
	compact,
	optional,
	textAreaClassName,
	labelClassName,
	fieldRef,
	...props
}: TextAreaProps) {
	const { error, getInputProps } = useField(name);

	return (
		<div className={cn('relative pt-4 mb-4', className)}>
			<div className="relative">
				<div className="flex">
					<textarea
						placeholder={placeholder || label}
						aria-invalid={!!error}
						aria-describedby={`${name}-error`}
						rows={rows || 4}
						ref={fieldRef}
						{...getInputProps({
							...props,
							id: name,
						})}
						className={cn(
							'bg-transparent rounded-md text-sm block w-full border-blue focus:ring-blue text-blue',
							error && 'border-red-600',
							textAreaClassName,
						)}
					/>
				</div>
				{label && (
					<label
						htmlFor={name}
						className={cn(
							'flex w-full items-center justify-between text-blue pointer-events-none',
							compact
								? 'absolute left-3 top-3 text-xs block peer-placeholder-shown:top-4 peer-placeholder-shown:left-3.5 peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-400 transition-all peer-focus:-translate-y-1/4 peer-focus:text-gray-700 peer-focus:text-xs peer-focus:left-3'
								: 'absolute left-0.5 -top-5 text-sm',
							labelClassName,
						)}
					>
						{label}
						{optional && <span className="text-xs mx-2 text-grey/80">(optional)</span>}
					</label>
				)}
				{!!error && (
					<div className="absolute pt-3 top-0 right-0 pr-3 flex pointer-events-none">
						<ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
					</div>
				)}
			</div>
			{error && (
				<span id={`${name}-error`} className="text-xs text-red-600">
					{error}
				</span>
			)}
		</div>
	);
}
